// Engagement_Dashboard_Old.service.ts
import {
  ApiContext,
  ApprovalStatusRow,
  DashboardFiltersPayload,
  OfficeStatusRow,
  PartnerStatusRow,
  SignatureStatusRow,
} from "./EngagementDashboardOld.utils";

export type DashboardDataBundle = {
  approvalStatus: ApprovalStatusRow[];
  signatureStatus: SignatureStatusRow[];
  office: OfficeStatusRow[];
  primaryPartner: PartnerStatusRow[];
};

const withAuthHeaders = (): HeadersInit => {
  // IMPORTANT:
  // Your backend reads headers.get("Authorization") and validates token.
  // If validate_token expects raw token (not "Bearer <token>"), remove "Bearer ".
  // If it's already working for other calls with Bearer, keep it.
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
};

const handleJson = async <T>(response: Response): Promise<ApiContext<T>> => {
  if (response.status === 401) {
    alert("Session ended, please log in again");
    return { success: false, message: "Unauthorized", data: undefined };
  }

  let json: any = null;
  try {
    json = await response.json();
  } catch {
    // If server returns non-JSON, we still want a useful error below
  }

  if (!response.ok) {
    const msg = json?.message || json?.error || response.statusText || "Request failed.";
    throw new Error(msg);
  }

  return (json || {}) as ApiContext<T>;
};

/**
 * Sanitize payload:
 * - Must be an object (never a list)
 * - Remove keys with empty arrays
 * - Remove "All"
 * - Remove falsy values
 *
 * Backend expects either:
 *  - omit the key
 *  - or send list of strings
 */
const normalizeFiltersPayload = (
  payload: DashboardFiltersPayload | null | undefined
): Record<string, string> => {
  // Always return a plain object with string values
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }

  const cleanAndStringify = (arr?: string[]) => {
    if (!Array.isArray(arr)) return undefined;

    const cleaned = arr
      .map((s) => (typeof s === "string" ? s.trim() : ""))
      .filter(Boolean)
      .filter((s) => s.toLowerCase() !== "all");

    return cleaned.length ? JSON.stringify(cleaned) : undefined;
  };

  const normalized: Record<string, string> = {};

  const bu = cleanAndStringify(payload.BusinessUnit);
  const office = cleanAndStringify(payload.Office);
  const partner = cleanAndStringify(payload.PrimaryPartner);
  const fy = cleanAndStringify(payload.FiscalYear);

  if (bu) normalized.BusinessUnit = bu;
  if (office) normalized.Office = office;
  if (partner) normalized.PrimaryPartner = partner;
  if (fy) normalized.FiscalYear = fy;

  return normalized;
};


const post = async <T>(
  url: string,
  payload?: DashboardFiltersPayload | null
): Promise<ApiContext<T>> => {
  const safePayload = normalizeFiltersPayload(payload);

  const response = await fetch(url, {
    method: "POST",
    headers: withAuthHeaders(),
    // MUST be an object JSON string, never []
    body: JSON.stringify(safePayload),
  });

  return handleJson<T>(response);
};

/**
 * API endpoints
 */
const endpoints = {
  approvalStatus: "/mdm/engagement-letter/dashboard/approval-status",
  signatureStatus: "/mdm/engagement-letter/dashboard/signature-status",
  office: "/mdm/engagement-letter/dashboard/office",
  primaryPartner: "/mdm/engagement-letter/dashboard/primary-partner",
};

/**
 * fetchDashboardData
 * - Backend does all filtering
 */
export const fetchDashboardData = async (
  baseUri: string,
  filtersPayload?: DashboardFiltersPayload | null
): Promise<DashboardDataBundle> => {
  const base = (baseUri || "").replace(/\/$/, "");

  const [approvalRes, signatureRes, officeRes, partnerRes] = await Promise.all([
    post<ApprovalStatusRow[]>(`${base}${endpoints.approvalStatus}`, filtersPayload),
    post<SignatureStatusRow[]>(`${base}${endpoints.signatureStatus}`, filtersPayload),
    post<OfficeStatusRow[]>(`${base}${endpoints.office}`, filtersPayload),
    post<PartnerStatusRow[]>(`${base}${endpoints.primaryPartner}`, filtersPayload),
  ]);

  return {
    approvalStatus: approvalRes.data || [],
    signatureStatus: signatureRes.data || [],
    office: officeRes.data || [],
    primaryPartner: partnerRes.data || [],
  };
};

/**
 * Optional individual fetches
 */
export const fetchApprovalStatusData = async (
  baseUri: string,
  filtersPayload?: DashboardFiltersPayload | null
): Promise<ApprovalStatusRow[]> => {
  const base = (baseUri || "").replace(/\/$/, "");
  const res = await post<ApprovalStatusRow[]>(`${base}${endpoints.approvalStatus}`, filtersPayload);
  return res.data || [];
};

export const fetchSignatureStatusData = async (
  baseUri: string,
  filtersPayload?: DashboardFiltersPayload | null
): Promise<SignatureStatusRow[]> => {
  const base = (baseUri || "").replace(/\/$/, "");
  const res = await post<SignatureStatusRow[]>(`${base}${endpoints.signatureStatus}`, filtersPayload);
  return res.data || [];
};

export const fetchOfficeData = async (
  baseUri: string,
  filtersPayload?: DashboardFiltersPayload | null
): Promise<OfficeStatusRow[]> => {
  const base = (baseUri || "").replace(/\/$/, "");
  const res = await post<OfficeStatusRow[]>(`${base}${endpoints.office}`, filtersPayload);
  return res.data || [];
};

export const fetchPrimaryPartnerData = async (
  baseUri: string,
  filtersPayload?: DashboardFiltersPayload | null
): Promise<PartnerStatusRow[]> => {
  const base = (baseUri || "").replace(/\/$/, "");
  const res = await post<PartnerStatusRow[]>(`${base}${endpoints.primaryPartner}`, filtersPayload);
  return res.data || [];
};
